import { useCallback, useEffect, useMemo, useRef } from 'react'

import { randomId } from '@copilotkit/shared'

import {
  type AppendMessage,
  F0AiChatTextArea,
  F0ClarifyingPanel,
  useAiChat as useF0AiChat,
  type WelcomeScreenSuggestion,
  type WelcomeScreenSuggestionItem,
} from '@factorialco/f0-react'

import { useClarifyingQuestion } from 'modules/ai/chat/providers/ClarifyingQuestionProvider'
import { useFactorialChatRuntime } from 'modules/ai/chat/providers/FactorialChatRuntimeProvider'
import { filterNonRenderableMessages } from 'modules/ai/chat/protocol/turnUtils'
import { serializeReplyQuoteTag } from 'modules/ai/chat/protocol/userMessageTags'

type SubmitPayload = {
  text: string
  files: Array<{ url: string; filename: string; mimetype: string }>
  context: { label: string; context: string } | null
  quote: { text: string } | null
}

type UserTextPart = { type: 'text'; text: string }
type UserBinaryPart = {
  type: 'binary'
  url: string
  filename: string
  mimeType: string
}

type ProcessDropFn = ((files: File[]) => void) | null
type WithInternalDropFn = { setProcessDroppedFilesFunction?: (fn: ProcessDropFn) => void }

const readInternalDropFn = (ui: object): ((fn: ProcessDropFn) => void) | undefined =>
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  (ui as WithInternalDropFn).setProcessDroppedFilesFunction

export const ConnectedChatInput = () => {
  const ui = useF0AiChat()
  const {
    placeholders,
    entityRefs,
    fileAttachments,
    pendingContext,
    setPendingContext,
    pendingQuote,
    setPendingQuote,
    disclaimer,
    footer,
    visualizationMode,
    creditWarning,
    welcomeScreenSuggestions,
    tracking,
  } = ui
  const { clarifyingQuestion } = useClarifyingQuestion()
  // `setProcessDroppedFilesFunction` is `@internal` in f0 and stripped
  // from the public `.d.ts`; the runtime method still exists.
  const setProcessDroppedFilesFunction = readInternalDropFn(ui)
  const {
    messages,
    inProgress,
    sendMessage,
    appendMessages,
    isLoadingThread,
    onBeforeSendMessage,
  } = useFactorialChatRuntime()
  const containerRef = useRef<HTMLDivElement>(null)

  const filteredMessages = useMemo(() => filterNonRenderableMessages(messages), [messages])
  const isWelcomeScreen = filteredMessages.length === 0 && !isLoadingThread
  const fullscreen = visualizationMode === 'fullscreen'

  useEffect(() => {
    const textarea = containerRef.current?.querySelector('textarea')
    textarea?.focus()
  }, [visualizationMode])

  const handleSubmit = useCallback(
    ({ text, files, context, quote }: SubmitPayload) => {
      // Agent expects the quote inline as a `<reply-quote>` tag; we keep
      // the plain text on `replyQuote` so f0 can render the chip.
      const visibleText = quote ? `${serializeReplyQuoteTag(quote.text)}${text}` : text
      const replyQuote = quote?.text

      // Multipart so the agent sees the pending context as its own part
      // instead of fused into the user's visible text.
      if (context || files.length > 0) {
        const contentParts: Array<UserTextPart | UserBinaryPart> = [
          ...(context
            ? [
                {
                  type: 'text' as const,
                  text: `<pending-context>${context.context}</pending-context>`,
                },
              ]
            : []),
          ...files.map((file) => ({
            type: 'binary' as const,
            url: file.url,
            filename: file.filename,
            mimeType: file.mimetype,
          })),
          { type: 'text' as const, text: visibleText },
        ]
        sendMessage({
          id: crypto.randomUUID(),
          role: 'user',
          // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-type-assertion/no-type-assertion
          content: contentParts as any,
          replyQuote,
        })
      } else if (quote) {
        sendMessage({
          id: crypto.randomUUID(),
          role: 'user',
          content: visibleText,
          replyQuote,
        })
      } else {
        sendMessage(text)
      }
    },
    [sendMessage]
  )

  const handleSuggestionClick = useCallback(
    (item: WelcomeScreenSuggestionItem, group: WelcomeScreenSuggestion) => {
      const prompt = item.prompt || item.title
      tracking?.onWelcomeSuggestionClick?.({ item, group, prompt })
      sendMessage({
        id: randomId(),
        role: 'user',
        content: prompt,
      })
    },
    [sendMessage, tracking]
  )

  const handleStop = useCallback(() => {
    // Route via appendMessages so the list gets JSON-sanitized first.
    // CopilotKit 1.54's setMessages calls structuredClone, which throws
    // on the live render wrappers attached to assistant messages.
    const stopMsg: AppendMessage = {
      role: 'assistant',
      content: `*<!--response-stopped-->Response stopped.*`,
    }
    appendMessages([stopMsg], { persist: false })
  }, [appendMessages])

  return (
    <F0AiChatTextArea
      ref={containerRef}
      onSubmit={handleSubmit}
      onStop={handleStop}
      inProgress={inProgress}
      onBeforeSubmit={onBeforeSendMessage}
      placeholders={placeholders}
      creditWarning={creditWarning}
      clarifyingUI={
        clarifyingQuestion ? (
          <F0ClarifyingPanel clarifyingQuestion={clarifyingQuestion} />
        ) : undefined
      }
      pendingContext={pendingContext}
      onPendingContextChange={setPendingContext}
      pendingQuote={pendingQuote}
      onPendingQuoteChange={setPendingQuote}
      fileAttachments={fileAttachments}
      searchPersons={entityRefs?.resolvers?.searchPersons}
      onProcessFilesRef={setProcessDroppedFilesFunction}
      disclaimer={disclaimer}
      footer={footer}
      isWelcomeScreen={isWelcomeScreen}
      fullscreen={fullscreen}
      welcomeScreenSuggestions={welcomeScreenSuggestions}
      onSuggestionClick={handleSuggestionClick}
    />
  )
}
